import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Landmark, Pencil, Trash2, X } from "lucide-react";

const empty = { name: "", bank_name: "", account_number: "", account_type: "Current", opening_balance: 0, currency: "INR" };

export default function Accounts() {
  const { companyId, currency } = useApp();
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [modal, setModal] = useState(null);
  const canManage = hasPermission("manage_accounts");

  const load = () => api.get(`/companies/${companyId}/accounts`).then(({ data }) => setAccounts(data)).catch(() => toast.error("Failed to load accounts"));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const save = async () => {
    const body = { ...modal, opening_balance: parseFloat(modal.opening_balance) || 0 };
    try {
      if (modal.id) await api.put(`/companies/${companyId}/accounts/${modal.id}`, body);
      else await api.post(`/companies/${companyId}/accounts`, body);
      toast.success("Account saved"); setModal(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    await api.delete(`/companies/${companyId}/accounts/${id}`); toast.success("Deleted"); load();
  };

  const totalBalance = accounts.reduce((s, a) => s + (a.current_balance || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="accounts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Accounts</h1>
          <p className="text-slate-500 text-sm mt-0.5">Total balance {formatMoney(totalBalance, currency)} across {accounts.length} accounts</p>
        </div>
        {canManage && (
          <button data-testid="add-account-btn" onClick={() => setModal({ ...empty })}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 active:scale-95 transition-all">
            <Plus size={18} /> Add Account
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((a) => (
          <div key={a.id} data-testid={`account-card-${a.id}`}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 grid place-items-center"><Landmark size={22} /></div>
              {canManage && (
                <div className="flex gap-1">
                  <button onClick={() => setModal({ ...a })} className="p-1.5 text-slate-400 hover:text-indigo-600"><Pencil size={15} /></button>
                  <button onClick={() => del(a.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </div>
              )}
            </div>
            <p className="font-heading font-bold text-slate-900 mt-4">{a.name}</p>
            <p className="text-sm text-slate-400">{a.bank_name} · {a.account_type}</p>
            <p className="text-xs text-slate-400 mt-1 font-mono">•••• {(a.account_number || "").slice(-4)}</p>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Current Balance</p>
              <p className="text-2xl font-heading font-extrabold text-slate-900 mt-1">{formatMoney(a.current_balance || 0, currency)}</p>
            </div>
          </div>
        ))}
        {accounts.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No accounts yet</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="account-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg">{modal.id ? "Edit" : "Add"} Account</h3>
              <button onClick={() => setModal(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Fld label="Account Name"><input data-testid="acct-name" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} className={inp} /></Fld>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Bank Name"><input value={modal.bank_name} onChange={(e) => setModal({ ...modal, bank_name: e.target.value })} className={inp} /></Fld>
                <Fld label="Type">
                  <select value={modal.account_type} onChange={(e) => setModal({ ...modal, account_type: e.target.value })} className={inp}>
                    <option>Current</option><option>Savings</option><option>Credit Card</option><option>Cash</option>
                  </select>
                </Fld>
              </div>
              <Fld label="Account Number"><input value={modal.account_number} onChange={(e) => setModal({ ...modal, account_number: e.target.value })} className={inp} /></Fld>
              <Fld label="Opening Balance (INR)"><input type="number" data-testid="acct-balance" value={modal.opening_balance} onChange={(e) => setModal({ ...modal, opening_balance: e.target.value })} className={inp} /></Fld>
            </div>
            <div className="p-5 border-t border-slate-100 flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
              <button data-testid="acct-save-btn" onClick={save} className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none";
const Fld = ({ label, children }) => (<div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>);
